import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  IconBack,
  InformationalScreen,
  LoadingModal,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { type FormikHelpers, useFormik } from 'formik';

import {
  FormContainer,
  InputPassword,
  MainContainer,
  PasswordInputValidator,
  WideBanner,
} from '~/components/utility';
import { ApiStatus, ErrorCode } from '~/constants';
import {
  useAccount,
  useApiError,
  useAuth,
  usePasswordValidation,
} from '~/hooks';
import { type RootState } from '~/reducers';
import { updatePassword } from '~/services/patient';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';

const mapStateToProps = ({
  config: { validation },
  auth: { account_id, include },
  env: { osType, osVersion, appVersion, deviceId },
}: RootState) => ({
  validation,
  accountId: account_id,
  deviceId,
  osType,
  osVersion,
  appVersion,
  patient: include?.patient,
});

const initialValuesFormik = {
  currentPassword: '',
  newPassword: '',
  repeatNewPassword: '',
};

type UpdatePasswordFormValues = typeof initialValuesFormik;

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const testID = 'update-password';

const UpdatePassword = ({
  validation,
  accountId,
  osType,
  osVersion,
  appVersion,
  patient,
  deviceId,
}: Props) => {
  const [meetsPasswordRequirements, setMeetsPasswordRequirements] =
    useState(false);

  const handleClickBack = useCallback(() => {
    mediator.publish('router:back');
  }, []);

  const { showPasswordValidationError } = useAccount('updatePassword');

  const { showApiErrorModal, showNetworkErrorModal } = useApiError();

  const {
    handleLoginError,
    handleLockoutError,
    showHcpError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage,
  } = useAuth('changepassword');

  const validationSchema = usePasswordValidation({
    hasNewPassword: true,
    validateCurrentPassword: true,
    patient,
    translations: {
      currentPassword: {
        required: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.errors.required',
        ),
      },
      newPassword: {
        required: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.required',
        ),
        minLength: () => '',
        // PasswordInputValidator handles this error message
        // if needed, uncomment the following lines to return the error message
        // i18n.t(
        //   'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.min',
        //   values
        // ),
        maxLength: (values) =>
          i18n.t(
            'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.max',
            values,
          ),
        trim: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.trim',
        ),
        repetitiveSequence: (values) =>
          i18n.t(
            'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.repetitiveSequence',
            values,
          ),
        context: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.context',
        ),
        dictionary: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.dictionary',
        ),
        repeatRequired: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.errors.required',
        ),
        mustMatch: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.errors.passwordMustMatch',
        ),
        differentFromCurrent: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.differentFromCurrent',
        ),
      },
    },
  });

  const handleClickForgotPass = () => {
    mediator.publish('router:navigate', '/forgot-password');
  };

  const handleApiRequestError = useCallback(
    (
      error: AxiosError<ApiErrorData>,
      resetForm: () => void,
      setFieldValue: FormikHelpers<UpdatePasswordFormValues>['setFieldValue'],
      setTouched: FormikHelpers<UpdatePasswordFormValues>['setTouched'],
    ) => {
      switch (error.status) {
        case ApiStatus.FORBIDDEN: {
          const code = error.response?.data.code;

          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
          }

          break;
        }

        case ApiStatus.UNAUTHORIZED:
          handleLoginError(error, resetForm, () => {
            void setFieldValue('currentPassword', '');
          });
          break;

        case ApiStatus.BAD_REQUEST: {
          const errors = error.response?.data.errors;

          void setFieldValue('newPassword', '');
          void setFieldValue('repeatNewPassword', '');

          void setTouched({});

          showPasswordValidationError({ validation }, errors);
          break;
        }

        case ApiStatus.TOO_MANY_REQUESTS: {
          handleLockoutError(error);
          resetForm();
          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [
      handleLockoutError,
      handleLoginError,
      showApiErrorModal,
      showHcpError,
      showNetworkErrorModal,
      showPasswordValidationError,
      validation,
    ],
  );

  const onSubmitForm = useCallback(
    async (
      values: UpdatePasswordFormValues,
      {
        setSubmitting,
        resetForm,
        setFieldValue,
        setTouched,
      }: FormikHelpers<UpdatePasswordFormValues>,
    ) => {
      try {
        await updatePassword({
          updatePasswordData: {
            userID: accountId,
            emailConfirmation: patient?.email ?? '',
            newPassword: values.newPassword,
            currentPassword: values.currentPassword,
          },
          deviceId,
          osType,
          osVersion,
          appVersion,
        });
        notifyApp('adc-webview:update-password');
      } catch (error) {
        handleApiRequestError(
          error as AxiosError<ApiErrorData>,
          resetForm,
          setFieldValue,
          setTouched,
        );
      }

      setSubmitting(false);
    },
    [
      accountId,
      appVersion,
      patient,
      osType,
      osVersion,
      deviceId,
      handleApiRequestError,
    ],
  );

  const {
    handleSubmit,
    handleBlur,
    handleChange,
    isValid,
    isSubmitting,
    errors,
    touched,
    values,
  } = useFormik({
    initialValues: initialValuesFormik,
    onSubmit: onSubmitForm,
    validationSchema,
    validateOnBlur: true,
  });

  return (
    <MainContainer testID={testID}>
      {isSubmitting && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}

      <InformationalScreen testID={buildTestId({ testID, suffix: 'screen' })}>
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
          topNavigationProps={{
            title: i18n.t('ChangePassword.title'),
            buttonStart: {
              element: <IconBack testID="nav-back" />,
              label: 'Go Back',
              onPress: handleClickBack,
            },
            testID: 'nav-back',
          }}
        >
          <InformationalScreen.Content>
            <FormContainer hasHiddenUserInput>
              {lockoutMessage ? (
                <WideBanner
                  testID={buildTestId({
                    testID,
                    suffix: ['lockout', 'banner'],
                  })}
                  message={lockoutMessage}
                  submessage={lockoutSubmessage}
                />
              ) : null}
              <InputPassword
                label={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.label',
                )}
                placeholder={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.placeholder',
                )}
                isInvalid={Boolean(
                  errors.currentPassword && touched.currentPassword,
                )}
                errorMessage={errors.currentPassword}
                testID={buildTestId({
                  testID,
                  suffix: ['current', 'password', 'input'],
                })}
                onChangeText={handleChange('currentPassword')}
                onBlur={handleBlur('currentPassword')}
                defaultValue={values.currentPassword}
                disabled={isLocked}
              />
              <Button
                variant="tertiary"
                label={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.links.forgotPasswordLink.label',
                )}
                onPress={handleClickForgotPass}
                testID={buildTestId({
                  testID,
                  suffix: ['forgot', 'password', 'button'],
                })}
              />
              <PasswordInputValidator
                fieldName="newPassword"
                placeholder={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.placeholder',
                )}
                label={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.label',
                )}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur('newPassword')}
                password={values.newPassword}
                onValidate={setMeetsPasswordRequirements}
                identities={validation?.identity}
                onChangeText={handleChange('newPassword')}
                testID={buildTestId({
                  testID,
                  suffix: ['new', 'password', 'input'],
                })}
                disabled={isLocked}
              />
              <InputPassword
                mt="$1"
                label={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.label',
                )}
                placeholder={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.placeholder',
                )}
                isInvalid={Boolean(
                  errors.repeatNewPassword && touched.repeatNewPassword,
                )}
                errorMessage={errors.repeatNewPassword}
                testID={buildTestId({
                  testID,
                  suffix: ['repeat', 'password', 'input'],
                })}
                onChangeText={handleChange('repeatNewPassword')}
                onBlur={handleBlur('repeatNewPassword')}
                defaultValue={values.repeatNewPassword}
                disabled={isLocked}
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label={i18n.t('Global.microcopy.common.save')}
              disabled={
                !values.currentPassword ||
                !values.newPassword ||
                !values.repeatNewPassword ||
                !isValid ||
                isSubmitting ||
                !meetsPasswordRequirements
              }
              onPress={() => {
                handleSubmit();
              }}
              testID={buildTestId({ testID, suffix: ['save', 'button'] })}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(UpdatePassword);
